import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Grid,
  Typography,
  Stack,
  Paper,
  Tooltip,
  IconButton,
  Avatar,
} from "@mui/material";
import {
  AccountBalanceWalletOutlined as WalletIcon,
  CasinoOutlined as LotteryIcon,
  CardGiftcardOutlined as GiftCardIcon,
  EmojiEventsOutlined as WinsIcon,
  AccountTreeOutlined as ReferralIcon,
  GroupOutlined as CreatorsIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import {
  Wallet,
  Gift,
  Coins,
  Copy,
  ExternalLink,
  Check,
  Search,
  Sparkles,
  ChevronRight,
  ShieldCheck,
  Building,
  User,
} from "lucide-react";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";

// Web3 & Contract Services
import cinstance from "../../Service/randomcinstance";
import usercinstance from "../../Service/usercinstance";
import giftcardcinstance from "../../Service/giftcardcinstance";
import web3 from "../../Service/web3";
import { networkID } from "../../../static/utils";
import {
  lotteryTypeMapper,
  resultStatusTypeMapper,
  LOTTERY_TYPES,
  STATUS_CONFIG,
  Path,
  getNetworkCurrency,
  getNetworkName,
} from "../../../static/Constants";
import { colors, customShadows, borderRadiusPx } from "../../../theme";

// Dashboard & UI Common Components
import { MetricCard, DashboardLayout } from "../../Components/dashboard";
import AdminLotteryCard from "../../Components/dashboard/lottery/AdminLotteryCard";

import {
  CommonButton,
  CommonChip,
  CommonTable,
  CommonInput,
  CommonSelect,
  CommonDialog,
  CommonTabs,
  CommonTab,
  CommonViewToggle,
} from "../../Components/ui";

const withTimeout = (promise, ms = 4000) => {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Request timed out")), ms)
    ),
  ]);
};

const safeFromWei = (val, unit = "ether") => {
  if (!val || val === "0" || val === 0) return "0";
  try {
    return web3.utils.fromWei(`${val}`, unit);
  } catch (err) {
    try {
      const num = Number(val);
      if (isNaN(num)) return "0";
      return (num / 1e18).toFixed(4);
    } catch {
      return "0";
    }
  }
};

const Profile = () => {
  const history = useHistory();
  const location = useLocation();

  // Navigation State
  const [activeTab, setActiveTab] = useState("overview"); // "overview" | "lotteries" | "giftcards"

  // Web3 & Account State
  const [account, setAccount] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [, setHasWeb3] = useState(true);
  const [copiedAddress, setCopiedAddress] = useState(false);
  const [copiedReferral, setCopiedReferral] = useState(false);
  const [networkId, setNetworkId] = useState(null);

  const currency = useMemo(() => getNetworkCurrency(networkId), [networkId]);
  const networkName = useMemo(() => getNetworkName(networkId), [networkId]);

  // Role Detection State
  const [isAdmin, setIsAdmin] = useState(false);
  const [isCreator, setIsCreator] = useState(false);
  const [orgData, setOrgData] = useState(null);

  // User Earnings & Balances
  const [lotteryTokensEarned, setLotteryTokensEarned] = useState("0"); // BET tokens
  const [giftCardTokensEarned, setGiftCardTokensEarned] = useState("0"); // BET tokens
  const [rawBetBalance, setRawBetBalance] = useState("0");
  const [referralBonusEarned, setReferralBonusEarned] = useState("0"); // POL
  const [creatorAmountEarned, setCreatorAmountEarned] = useState("0"); // POL
  const [adminRegistrationFees, setAdminRegistrationFees] = useState("0"); // POL

  // Collections State
  const [lotteryList, setLotteryList] = useState([]);
  const [giftCardList, setGiftCardList] = useState([]);

  // Filter & Search State
  const [lotterySearch, setLotterySearch] = useState("");
  const [lotteryTypeFilter, setLotteryTypeFilter] = useState("all");
  const [lotteryViewMode, setLotteryViewMode] = useState("table");

  const [giftCardSearch, setGiftCardSearch] = useState("");
  const [giftCardTypeFilter, setGiftCardTypeFilter] = useState("all");

  // Withdrawal Loading States & Dialogs
  const [withdrawType, setWithdrawType] = useState(null); // "lottery" | "giftcard" | "referral" | "creator" | "admin"
  const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);

  // Parse URL search params (e.g. ?giftCardPurchased=true, ?tab=..., ?role=admin|creator)
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const roleQueryParam = searchParams.get("role");
  const locationStateRole = location.state?.role;

  // Active dashboard role resolution:
  // 1. Explicit query parameter (?role=admin or ?role=creator)
  // 2. React Router history state (location.state.role)
  // 3. Fallback to contract detected role (isAdmin -> "admin", isCreator -> "creator", default -> "creator")
  const activeDashboardRole = useMemo(() => {
    if (roleQueryParam === "admin" || locationStateRole === "admin") return "admin";
    if (roleQueryParam === "creator" || locationStateRole === "creator") return "creator";
    if (isAdmin) return "admin";
    if (isCreator) return "creator";
    return "creator";
  }, [roleQueryParam, locationStateRole, isAdmin, isCreator]);

  useEffect(() => {
    const giftCardPurchased = searchParams.get("giftCardPurchased");
    const tabParam = searchParams.get("tab");

    if (giftCardPurchased === "true") {
      setActiveTab("giftcards");
    } else if (tabParam && ["overview", "lotteries", "giftcards"].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [location.search, searchParams]);

  // Main Data Fetching Callback
  const fetchProfileData = useCallback(async () => {
    if (!window.ethereum) {
      setHasWeb3(false);
      setLoading(false);
      return;
    }

    try {
      const accounts = await web3.eth.getAccounts();
      if (!accounts || accounts.length === 0) {
        setAccount("");
        setLoading(false);
        return;
      }

      const currentAccount = accounts[0];
      setAccount(currentAccount);
      setHasWeb3(true);

      // Fetch active network ID
      try {
        if (web3?.eth?.net) {
          const netId = await withTimeout(web3.eth.net.getId(), 2500);
          if (netId) setNetworkId(netId);
        }
      } catch (netErr) {
        console.warn("Error fetching netId in Profile:", netErr);
      }

      // 1. Role Detection (Admin & Creator)
      let detectedAdmin = false;
      let detectedCreator = false;

      try {
        let contractAdmin = "";
        if (cinstance?.methods?.admin) {
          contractAdmin = await withTimeout(cinstance.methods.admin().call(), 3000);
        }
        detectedAdmin = Boolean(
          contractAdmin &&
          currentAccount &&
          contractAdmin.toLowerCase() === currentAccount.toLowerCase()
        );
      } catch (err) {
        console.warn("Error fetching contract admin:", err);
      }
      setIsAdmin(detectedAdmin);

      try {
        if (usercinstance?.methods?.organisationbyaddr) {
          const orgRes = await withTimeout(
            usercinstance.methods.organisationbyaddr(currentAccount).call(),
            3000
          );
          if (
            orgRes &&
            orgRes.active &&
            orgRes.userAddress &&
            orgRes.userAddress.toLowerCase() === currentAccount.toLowerCase()
          ) {
            detectedCreator = true;
            setOrgData(orgRes);
            if (orgRes.amountEarned) {
              setCreatorAmountEarned(safeFromWei(orgRes.amountEarned));
            }
          } else {
            setIsCreator(false);
            setOrgData(null);
          }
        }
      } catch (err) {
        console.warn("Error fetching organisation details:", err);
      }
      setIsCreator(detectedCreator);

      // 2. Fetch User Earnings & Commission Balances
      const balanceTasks = [
        // Lottery Winning Tokens (BET)
        (async () => {
          try {
            if (cinstance?.methods?.tokenearned) {
              const res = await withTimeout(
                cinstance.methods.tokenearned(currentAccount).call(),
                3500
              );
              setLotteryTokensEarned(safeFromWei(res));
            }
          } catch (e) {
            console.warn("Error tokenearned:", e);
          }
        })(),

        // Gift Card BET Token Balance
        (async () => {
          try {
            if (giftcardcinstance?.methods?.getBetTokenBalance) {
              const res = await withTimeout(
                giftcardcinstance.methods.getBetTokenBalance(currentAccount).call(),
                3500
              );
              setRawBetBalance(res || "0");
              setGiftCardTokensEarned(safeFromWei(res));
            }
          } catch (e) {
            console.warn("Error getBetTokenBalance:", e);
          }
        })(),

        // Referral Bonus Commission (POL)
        (async () => {
          try {
            if (cinstance?.methods?.refereeEarned) {
              const res = await withTimeout(
                cinstance.methods.refereeEarned(currentAccount).call(),
                3500
              );
              setReferralBonusEarned(safeFromWei(res));
            }
          } catch (e) {
            console.warn("Error refereeEarned:", e);
          }
        })(),

        // Admin Registration Fees (if Admin)
        (async () => {
          if (detectedAdmin && usercinstance?.methods?.registrationFees) {
            try {
              const res = await withTimeout(
                usercinstance.methods.registrationFees(currentAccount).call(),
                3500
              );
              setAdminRegistrationFees(safeFromWei(res));
            } catch (e) {
              console.warn("Error registrationFees:", e);
            }
          }
        })(),
      ];

      await Promise.allSettled(balanceTasks);

      // 3. Fetch User Lotteries
      try {
        if (cinstance?.methods?.getUserlotteries) {
          const userLotteryIds = await withTimeout(
            cinstance.methods.getUserlotteries(currentAccount).call(),
            4000
          );

          if (Array.isArray(userLotteryIds) && userLotteryIds.length > 0) {
            const detailPromises = userLotteryIds.map((id) =>
              withTimeout(cinstance.methods.lottery(id).call(), 3000).catch(() => null)
            );
            const lotteriesRes = await Promise.allSettled(detailPromises);
            const validLotteries = lotteriesRes
              .filter((r) => r.status === "fulfilled" && r.value)
              .map((r) => r.value);
            setLotteryList(validLotteries);
          } else {
            setLotteryList([]);
          }
        }
      } catch (err) {
        console.warn("Error fetching user lotteries:", err);
        setLotteryList([]);
      }

      // 4. Fetch User Gift Cards
      try {
        if (giftcardcinstance?.methods?.getGiftCardsByBuyer) {
          const buyerCards = await withTimeout(
            giftcardcinstance.methods.getGiftCardsByBuyer(currentAccount).call(),
            4000
          );
          if (Array.isArray(buyerCards)) {
            const flattened = Array.isArray(buyerCards[0]) ? buyerCards[0] : buyerCards;
            setGiftCardList(flattened || []);
          } else {
            setGiftCardList([]);
          }
        }
      } catch (err) {
        console.warn("Error fetching buyer gift cards:", err);
        setGiftCardList([]);
      }
    } catch (err) {
      console.error("Profile initial data load error:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchProfileData();

    // Listen for Web3 account or network changes
    if (window.ethereum) {
      const handleAccountsChanged = (accounts) => {
        if (accounts.length > 0) {
          setAccount(accounts[0]);
          fetchProfileData();
        } else {
          setAccount("");
        }
      };

      const handleChainChanged = (chainId) => {
        if (chainId) {
          const parsed = parseInt(chainId, 16);
          if (!isNaN(parsed)) setNetworkId(parsed);
        }
        fetchProfileData();
      };

      window.ethereum.on("accountsChanged", handleAccountsChanged);
      window.ethereum.on("chainChanged", handleChainChanged);

      return () => {
        if (window.ethereum.removeListener) {
          window.ethereum.removeListener("accountsChanged", handleAccountsChanged);
          window.ethereum.removeListener("chainChanged", handleChainChanged);
        }
      };
    }
  }, [fetchProfileData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchProfileData();
    toast.info("Profile and balance data refreshed!");
  };

  const handleCopy = (text, type) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    if (type === "address") {
      setCopiedAddress(true);
      setTimeout(() => setCopiedAddress(false), 2000);
      toast.success("Wallet address copied to clipboard!");
    } else if (type === "referral") {
      setCopiedReferral(true);
      setTimeout(() => setCopiedReferral(false), 2000);
      toast.success("Referral invitation link copied to clipboard!");
    }
  };

  // Open Withdrawal Dialog
  const handleOpenWithdraw = (type) => {
    setWithdrawType(type);
    setWithdrawModalOpen(true);
  };

  // Execute On-Chain Withdrawal
  const handleConfirmWithdraw = async () => {
    if (!account) {
      toast.error("Please connect your Web3 wallet first.");
      return;
    }

    try {
      setWithdrawing(true);

      if (withdrawType === "lottery") {
        await cinstance.methods.redeemTokens().send({ from: account });
        toast.success("Lottery BET tokens redeemed successfully!");
      } else if (withdrawType === "giftcard") {
        await giftcardcinstance.methods
          .withdrawUserBetTokens(account, rawBetBalance)
          .send({ from: account });
        toast.success("Gift Card BET tokens withdrawn successfully!");
      } else if (withdrawType === "referral") {
        await cinstance.methods.withdrawrefereecommission().send({ from: account });
        toast.success("Referral bonus commission withdrawn successfully!");
      } else if (withdrawType === "creator") {
        await cinstance.methods.withdrawcommission().send({ from: account });
        toast.success("Creator ticket commission withdrawn successfully!");
      } else if (withdrawType === "admin") {
        if (cinstance?.methods?.withdrawAdmin) {
          await cinstance.methods.withdrawAdmin().send({ from: account });
        }
        if (usercinstance?.methods?.withdrawRegisterationFees) {
          await usercinstance.methods.withdrawRegisterationFees().send({ from: account });
        }
        toast.success("Platform earnings & registration fees withdrawn successfully!");
      }

      setWithdrawModalOpen(false);
      fetchProfileData();
    } catch (err) {
      console.error("Withdrawal transaction error:", err);
      toast.error(err?.message || "Transaction failed or rejected.");
    } finally {
      setWithdrawing(false);
    }
  };

  // Filtered Lotteries
  const filteredLotteries = useMemo(() => {
    return lotteryList.filter((item) => {
      const matchesSearch =
        !lotterySearch ||
        `${item.lotteryId}`.includes(lotterySearch.toLowerCase()) ||
        lotteryTypeMapper[item.lotteryType]?.toLowerCase().includes(lotterySearch.toLowerCase());

      const matchesType =
        lotteryTypeFilter === "all" || `${item.lotteryType}` === `${lotteryTypeFilter}`;

      return matchesSearch && matchesType;
    });
  }, [lotteryList, lotterySearch, lotteryTypeFilter]);

  // Filtered Gift Cards
  const filteredGiftCards = useMemo(() => {
    return giftCardList.filter((card) => {
      const matchesSearch =
        !giftCardSearch ||
        `${card.giftCardId}`.includes(giftCardSearch.toLowerCase()) ||
        (card.tokenName && card.tokenName.toLowerCase().includes(giftCardSearch.toLowerCase()));

      const matchesType =
        giftCardTypeFilter === "all" ||
        (giftCardTypeFilter === "donation" && card.isDonation === true) ||
        (giftCardTypeFilter === "sell" && !card.isDonation);

      return matchesSearch && matchesType;
    });
  }, [giftCardList, giftCardSearch, giftCardTypeFilter]);

  const userDisplayName = activeDashboardRole === "admin"
    ? "AutoBET Admin"
    : isCreator
    ? orgData?.name || "Verified Creator"
    : account
    ? `Creator (${account.slice(0, 6)}…${account.slice(-4)})`
    : "Creator Dashboard";

  const userRoleLabel = activeDashboardRole === "admin"
    ? "System Administrator"
    : isCreator
    ? "Verified Creator"
    : "Creator";

  const referralUrl = account ? `https://autobet.io/?ref=${account}` : "https://autobet.io";

  // Table Columns Definition for My Lotteries
  const lotteryColumns = [
    {
      id: "lotteryId",
      label: "Lottery ID",
      render: (_, row) => (
        <Box>
          <Typography sx={{ fontWeight: 800, color: colors.slate[900], fontSize: "14px" }}>
            #{row.lotteryId}
          </Typography>
          <Typography sx={{ fontSize: "11px", color: colors.slate[400], fontFamily: "monospace" }}>
            {row.deployedOn ? moment.unix(row.deployedOn).format("DD/MM/YYYY") : "N/A"}
          </Typography>
        </Box>
      ),
    },
    {
      id: "lotteryType",
      label: "Game Type",
      render: (_, row) => {
        const typeConfig = LOTTERY_TYPES[row.lotteryType] || LOTTERY_TYPES[0];
        const TypeIcon = typeConfig.icon;
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: borderRadiusPx.sm,
                bgcolor: typeConfig.bgColor,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: typeConfig.color,
              }}
            >
              <TypeIcon sx={{ fontSize: 16 }} />
            </Box>
            <Typography sx={{ fontWeight: 600, color: colors.slate[800], fontSize: "13px" }}>
              {typeConfig.name}
            </Typography>
          </Box>
        );
      },
    },
    {
      id: "entryFee",
      label: "Entry Fee",
      align: "right",
      render: (_, row) => (
        <Typography sx={{ fontWeight: 700, color: colors.primary.main, fontSize: "13px" }}>
          {safeFromWei(row.entryFee)} {currency}
        </Typography>
      ),
    },
    {
      id: "totalPrize",
      label: "Prize Pool",
      align: "right",
      render: (_, row) => (
        <Typography sx={{ fontWeight: 700, color: colors.emerald[600], fontSize: "13px" }}>
          {safeFromWei(row.totalPrize)} {currency}
        </Typography>
      ),
    },
    {
      id: "status",
      label: "Status",
      render: (_, row) => {
        const statusInfo = STATUS_CONFIG[row.status] || {
          label: resultStatusTypeMapper[row.status] || "Active",
          variant: "neutral",
        };
        return <CommonChip label={statusInfo.label} variant={statusInfo.variant} size="small" />;
      },
    },
    {
      id: "actions",
      label: "Action",
      align: "right",
      render: (_, row) => (
        <CommonButton
          variant="outline"
          size="small"
          onClick={() => history.push(Path.getLotteryDetails(row.lotteryId))}
          endIcon={<ChevronRight size={14} />}
        >
          View
        </CommonButton>
      ),
    },
  ];

  // Table Columns Definition for My Gift Cards
  const giftCardColumns = [
    {
      id: "giftCardId",
      label: "Card ID",
      render: (_, row) => (
        <Typography sx={{ fontWeight: 800, color: colors.slate[900], fontSize: "14px" }}>
          #{row.giftCardId}
        </Typography>
      ),
    },
    {
      id: "type",
      label: "Card Type",
      render: (_, row) => (
        <CommonChip
          label={row.isDonation ? "Donation" : "Standard Purchase"}
          variant={row.isDonation ? "success" : "primary"}
          size="small"
        />
      ),
    },
    {
      id: "betValue",
      label: "Tokens Received",
      align: "right",
      render: (_, row) => (
        <Typography sx={{ fontWeight: 700, color: colors.emerald[600], fontSize: "13px" }}>
          {safeFromWei(row.BETValue)} BET
        </Typography>
      ),
    },
    {
      id: "feePaid",
      label: "Price Paid",
      align: "right",
      render: (_, row) => (
        <Typography sx={{ fontWeight: 700, color: colors.slate[800], fontSize: "13px" }}>
          {safeFromWei(row.ETHPrice)} {currency}
        </Typography>
      ),
    },
    {
      id: "actions",
      label: "Action",
      align: "right",
      render: (_, row) => (
        <CommonButton
          variant="outline"
          size="small"
          onClick={() => history.push(Path.getGiftCardDetails(row.giftCardId))}
          endIcon={<ExternalLink size={14} />}
        >
          Details
        </CommonButton>
      ),
    },
  ];

  return (
    <DashboardLayout
      role={activeDashboardRole}
      title="Profile & Settings"
      subtitle="Manage your connected wallet, withdrawable winnings, referral bonuses, and platform activity."
      breadcrumbs={[
        {
          label: "Dashboard",
          href: activeDashboardRole === "admin" ? Path.admin : Path.org,
        },
        { label: "Profile & Settings" },
      ]}
      headerActions={
        <Stack direction="row" spacing={1.5} alignItems="center">
          <CommonButton
            variant="refresh"
            onClick={handleRefresh}
            loading={refreshing || loading}
          />
        </Stack>
      }
      creatorAddress={account}
      userName={userDisplayName}
      userRole={userRoleLabel}
    >
      {/* SECTION: Connected Profile Banner & Quick Role Metadata */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.5, sm: 3.5 },
              mb: 3.5,
              borderRadius: borderRadiusPx.xl,
              border: `1px solid ${colors.dashboard.cardBorder}`,
              boxShadow: customShadows.cardSubtle,
              background: `linear-gradient(135deg, ${colors.background.paper} 0%, ${colors.slate[50]} 100%)`,
            }}
          >
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={8}>
                <Stack direction="row" spacing={2.5} alignItems="center">
                  <Avatar
                    sx={{
                      width: { xs: 68, sm: 80 },
                      height: { xs: 68, sm: 80 },
                      background: activeDashboardRole === "admin"
                        ? "linear-gradient(135deg, #1e1b4b 0%, #312e81 45%, #4338ca 100%)"
                        : isCreator
                        ? "linear-gradient(135deg, #4a044e 0%, #86198f 45%, #c026d3 100%)"
                        : "linear-gradient(135deg, #0369a1 0%, #0284c7 45%, #0ea5e9 100%)",
                      boxShadow: activeDashboardRole === "admin"
                        ? "0 8px 24px rgba(67, 56, 202, 0.45)"
                        : isCreator
                        ? "0 8px 24px rgba(192, 38, 211, 0.45)"
                        : "0 8px 24px rgba(2, 132, 199, 0.45)",
                      border: `3px solid ${
                        activeDashboardRole === "admin" ? "#818cf8" : isCreator ? "#f472b6" : "#7dd3fc"
                      }`,
                      color: "#ffffff",
                    }}
                  >
                    {activeDashboardRole === "admin" ? (
                      <ShieldCheck size={38} color="#c7d2fe" strokeWidth={2.2} />
                    ) : isCreator ? (
                      <Sparkles size={38} color="#fdf4ff" strokeWidth={2.2} />
                    ) : (
                      <User size={38} color="#f0f9ff" strokeWidth={2.2} />
                    )}
                  </Avatar>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.75 }}>
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: 800, color: colors.slate[900], fontSize: { xs: "1.25rem", sm: "1.45rem" } }}
                      >
                        {userDisplayName}
                      </Typography>

                      {activeDashboardRole === "admin" && (
                        <CommonChip
                          label="Administrator"
                          variant="danger"
                          size="small"
                          startIcon={<ShieldCheck size={14} />}
                        />
                      )}

                      {activeDashboardRole !== "admin" && isCreator && (
                        <CommonChip
                          label="Verified Creator"
                          variant="warning"
                          size="small"
                          startIcon={<Building size={14} />}
                        />
                      )}

                      {activeDashboardRole !== "admin" && !isCreator && (
                        <CommonChip
                          label="Creator Dashboard"
                          variant="primary"
                          size="small"
                          startIcon={<Sparkles size={14} />}
                        />
                      )}
                    </Stack>

                    <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.75,
                          px: 1.25,
                          py: 0.4,
                          borderRadius: borderRadiusPx.sm,
                          bgcolor: colors.slate[100],
                          border: `1px solid ${colors.slate[200]}`,
                        }}
                      >
                        <Wallet size={14} color={colors.slate[500]} />
                        <Typography
                          sx={{
                            fontFamily: "monospace",
                            fontSize: "0.82rem",
                            fontWeight: 600,
                            color: colors.slate[700],
                          }}
                        >
                          {account ? `${account.slice(0, 8)}...${account.slice(-6)}` : "Wallet Not Connected"}
                        </Typography>
                        {account && (
                          <Tooltip title={copiedAddress ? "Copied!" : "Copy Wallet Address"} arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleCopy(account, "address")}
                              sx={{ p: 0.2 }}
                            >
                              {copiedAddress ? <Check size={14} color={colors.emerald[600]} /> : <Copy size={14} />}
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>

                      <CommonChip
                        label={`Network: ${networkName}`}
                        variant="neutral"
                        size="small"
                      />
                    </Stack>
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          </Paper>

          {/* Role Hubs for Creator & Admin */}
          {activeDashboardRole !== "admin" && isCreator && orgData && (
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                mb: 3.5,
                borderRadius: borderRadiusPx.xl,
                border: `1px solid ${colors.amber[200]}`,
                bgcolor: colors.amber[50],
                boxShadow: customShadows.cardSubtle,
              }}
            >
              <Grid container spacing={2.5} alignItems="center">
                <Grid item xs={12} md={7}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
                    <Building size={20} color={colors.amber[700]} />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: colors.amber[900] }}>
                      Creator Hub: {orgData.name || "Autobet Partner"}
                    </Typography>
                  </Stack>
                  <Typography sx={{ fontSize: "0.82rem", color: colors.amber[800] }}>
                    Organisation ID: <strong>#{orgData.id}</strong> • Allowed Prize Range:{" "}
                    <strong>{orgData.minPrize || 0} {currency}</strong> to <strong>{orgData.maxPrize || "Unlimited"} {currency}</strong>
                  </Typography>
                </Grid>
                <Grid item xs={12} md={5}>
                  <Stack direction="row" gap={1.25} justifyContent={{ xs: "flex-start", md: "flex-end" }} flexWrap="wrap">
                    <CommonButton
                      variant="outline"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => history.push(Path.orgcreatelottery)}
                    >
                      Create Lottery
                    </CommonButton>
                    <CommonButton
                      variant="outline"
                      size="small"
                      startIcon={<Gift size={14} />}
                      onClick={() => history.push(Path.orgcreatecard)}
                    >
                      Create Gift Card
                    </CommonButton>
                    <CommonButton
                      variant="primary"
                      size="small"
                      onClick={() => history.push(Path.org)}
                    >
                      Creator Dashboard
                    </CommonButton>
                  </Stack>
                </Grid>
              </Grid>
            </Paper>
          )}

          {activeDashboardRole === "admin" && (
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                mb: 3.5,
                borderRadius: borderRadiusPx.xl,
                border: `1px solid ${colors.blue[200]}`,
                bgcolor: colors.blue[50],
                boxShadow: customShadows.cardSubtle,
              }}
            >
              <Grid container spacing={2.5} alignItems="center">
                <Grid item xs={12} md={7}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
                    <ShieldCheck size={20} color={colors.primary.main} />
                    <Typography variant="h6" sx={{ fontWeight: 700, color: colors.primary.dark }}>
                      System Administrator Hub
                    </Typography>
                  </Stack>
                  <Typography sx={{ fontSize: "0.82rem", color: colors.slate[700] }}>
                    You have verified owner privileges over AutoBET lottery, organisation registries, and platform treasuries.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={5}>
                  <Stack direction="row" gap={1.25} justifyContent={{ xs: "flex-start", md: "flex-end" }} flexWrap="wrap">
                    <CommonButton
                      variant="outline"
                      size="small"
                      startIcon={<CreatorsIcon sx={{ fontSize: 16 }} />}
                      onClick={() => history.push(Path.adminorglist)}
                    >
                      Creators
                    </CommonButton>
                    <CommonButton
                      variant="outline"
                      size="small"
                      startIcon={<LotteryIcon sx={{ fontSize: 16 }} />}
                      onClick={() => history.push(Path.adminlotterylist)}
                    >
                      Lotteries
                    </CommonButton>
                    <CommonButton
                      variant="primary"
                      size="small"
                      onClick={() => history.push(Path.admin)}
                    >
                      Admin Console
                    </CommonButton>
                  </Stack>
                </Grid>
              </Grid>
            </Paper>
          )}

          {/* Navigation Tabs */}
          <CommonTabs
            value={activeTab}
            onChange={(_, val) => setActiveTab(val)}
            sx={{ mb: 3.5 }}
          >
            <CommonTab
              value="overview"
              label="Overview & Balances"
              icon={<WalletIcon sx={{ fontSize: 18 }} />}
            />
            <CommonTab
              value="lotteries"
              label="My Lotteries"
              count={lotteryList.length}
              icon={<LotteryIcon sx={{ fontSize: 18 }} />}
            />
            <CommonTab
              value="giftcards"
              label="My Gift Cards"
              count={giftCardList.length}
              icon={<GiftCardIcon sx={{ fontSize: 18 }} />}
            />
          </CommonTabs>

          {/* TAB 1: OVERVIEW & BALANCES */}
          {activeTab === "overview" && (
            <Box>
              {/* Earnings & Treasury Metric Ribbon */}
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: "1.1rem",
                  color: colors.dashboard.title,
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Coins size={20} color={colors.primary.main} />
                Withdrawable Earnings & Commission Balances
              </Typography>

              <Grid container spacing={2.5} sx={{ mb: 4 }}>
                {/* 1. Lottery Winning Tokens (BET) */}
                <Grid item xs={12} sm={6} lg={4}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: borderRadiusPx.xl,
                      border: `1px solid ${colors.dashboard.cardBorder}`,
                      bgcolor: colors.background.paper,
                      boxShadow: customShadows.cardSubtle,
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box sx={{ mb: 2 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                        <Box
                          sx={{
                            width: 38,
                            height: 38,
                            borderRadius: borderRadiusPx.md,
                            bgcolor: colors.emerald[50],
                            color: colors.emerald[600],
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <WinsIcon />
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: "0.8rem", color: colors.slate[500], fontWeight: 600 }}>
                            Lottery Winnings
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 800, color: colors.slate[900] }}>
                            {Number(lotteryTokensEarned).toFixed(4)} <span style={{ fontSize: "0.85rem", color: colors.emerald[600] }}>BET</span>
                          </Typography>
                        </Box>
                      </Stack>
                      <Typography sx={{ fontSize: "0.76rem", color: colors.slate[500] }}>
                        Accumulated BET tokens earned from winning smart contract lottery draws.
                      </Typography>
                    </Box>

                    <CommonButton
                      variant="primary"
                      size="small"
                      fullWidth
                      disabled={Number(lotteryTokensEarned) <= 0}
                      onClick={() => handleOpenWithdraw("lottery")}
                    >
                      Redeem Tokens
                    </CommonButton>
                  </Paper>
                </Grid>

                {/* 2. Gift Card Tokens (BET) */}
                <Grid item xs={12} sm={6} lg={4}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: borderRadiusPx.xl,
                      border: `1px solid ${colors.dashboard.cardBorder}`,
                      bgcolor: colors.background.paper,
                      boxShadow: customShadows.cardSubtle,
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box sx={{ mb: 2 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                        <Box
                          sx={{
                            width: 38,
                            height: 38,
                            borderRadius: borderRadiusPx.md,
                            bgcolor: colors.blue[50],
                            color: colors.primary.main,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <GiftCardIcon />
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: "0.8rem", color: colors.slate[500], fontWeight: 600 }}>
                            Gift Card Balance
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 800, color: colors.slate[900] }}>
                            {Number(giftCardTokensEarned).toFixed(4)} <span style={{ fontSize: "0.85rem", color: colors.primary.main }}>BET</span>
                          </Typography>
                        </Box>
                      </Stack>
                      <Typography sx={{ fontSize: "0.76rem", color: colors.slate[500] }}>
                        Tokens accumulated and available to withdraw from purchased BetCards.
                      </Typography>
                    </Box>

                    <CommonButton
                      variant="primary"
                      size="small"
                      fullWidth
                      disabled={Number(giftCardTokensEarned) <= 0}
                      onClick={() => handleOpenWithdraw("giftcard")}
                    >
                      Withdraw BET
                    </CommonButton>
                  </Paper>
                </Grid>

                {/* 3. Referral Commission (POL) */}
                <Grid item xs={12} sm={6} lg={4}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: borderRadiusPx.xl,
                      border: `1px solid ${colors.dashboard.cardBorder}`,
                      bgcolor: colors.background.paper,
                      boxShadow: customShadows.cardSubtle,
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box sx={{ mb: 2 }}>
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                        <Box
                          sx={{
                            width: 38,
                            height: 38,
                            borderRadius: borderRadiusPx.md,
                            bgcolor: colors.amber[50],
                            color: colors.amber[600],
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <ReferralIcon />
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: "0.8rem", color: colors.slate[500], fontWeight: 600 }}>
                            Referral Commission
                          </Typography>
                          <Typography variant="h5" sx={{ fontWeight: 800, color: colors.slate[900] }}>
                            {Number(referralBonusEarned).toFixed(4)} <span style={{ fontSize: "0.85rem", color: colors.amber[600] }}>{currency}</span>
                          </Typography>
                        </Box>
                      </Stack>
                      <Typography sx={{ fontSize: "0.76rem", color: colors.slate[500] }}>
                        Native {currency} rewards earned from player registrations and referee purchases.
                      </Typography>
                    </Box>

                    <CommonButton
                      variant="primary"
                      size="small"
                      fullWidth
                      disabled={Number(referralBonusEarned) <= 0}
                      onClick={() => handleOpenWithdraw("referral")}
                    >
                      Withdraw Commission
                    </CommonButton>
                  </Paper>
                </Grid>

                {/* 4. Creator Ticket Commission (if Creator) */}
                {isCreator && (
                  <Grid item xs={12} sm={6} lg={4}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        borderRadius: borderRadiusPx.xl,
                        border: `1px solid ${colors.amber[300]}`,
                        bgcolor: colors.amber[50],
                        boxShadow: customShadows.cardSubtle,
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box sx={{ mb: 2 }}>
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                          <Box
                            sx={{
                              width: 38,
                              height: 38,
                              borderRadius: borderRadiusPx.md,
                              bgcolor: colors.amber[100],
                              color: colors.amber[700],
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Building size={20} />
                          </Box>
                          <Box>
                            <Typography sx={{ fontSize: "0.8rem", color: colors.amber[800], fontWeight: 600 }}>
                              Creator Ticket Earnings
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 800, color: colors.amber[900] }}>
                              {Number(creatorAmountEarned).toFixed(4)} <span style={{ fontSize: "0.85rem", color: colors.amber[700] }}>{currency}</span>
                            </Typography>
                          </Box>
                        </Stack>
                        <Typography sx={{ fontSize: "0.76rem", color: colors.amber[800] }}>
                          Accumulated ticket sales revenue from lotteries created by your dashboard.
                        </Typography>
                      </Box>

                      <CommonButton
                        variant="primary"
                        size="small"
                        fullWidth
                        disabled={Number(creatorAmountEarned) <= 0}
                        onClick={() => handleOpenWithdraw("creator")}
                      >
                        Withdraw Ticket Commission
                      </CommonButton>
                    </Paper>
                  </Grid>
                )}

                {/* 5. Admin Platform Treasury (if Admin) */}
                {(activeDashboardRole === "admin" || isAdmin) && (
                  <Grid item xs={12} sm={6} lg={4}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        borderRadius: borderRadiusPx.xl,
                        border: `1px solid ${colors.blue[300]}`,
                        bgcolor: colors.blue[50],
                        boxShadow: customShadows.cardSubtle,
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box sx={{ mb: 2 }}>
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                          <Box
                            sx={{
                              width: 38,
                              height: 38,
                              borderRadius: borderRadiusPx.md,
                              bgcolor: colors.blue[100],
                              color: colors.primary.main,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <ShieldCheck size={20} />
                          </Box>
                          <Box>
                            <Typography sx={{ fontSize: "0.8rem", color: colors.primary.dark, fontWeight: 600 }}>
                              Admin Registration Fees
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 800, color: colors.primary.dark }}>
                              {Number(adminRegistrationFees).toFixed(4)} <span style={{ fontSize: "0.85rem", color: colors.primary.main }}>{currency}</span>
                            </Typography>
                          </Box>
                        </Stack>
                        <Typography sx={{ fontSize: "0.76rem", color: colors.slate[600] }}>
                          Platform registration fees and smart contract treasury earnings.
                        </Typography>
                      </Box>

                      <CommonButton
                        variant="primary"
                        size="small"
                        fullWidth
                        onClick={() => handleOpenWithdraw("admin")}
                      >
                        Withdraw Admin Fees
                      </CommonButton>
                    </Paper>
                  </Grid>
                )}
              </Grid>

              {/* Quick Summary Cards */}
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={4}>
                  <MetricCard
                    title="Entered Lotteries"
                    value={lotteryList.length}
                    icon={<LotteryIcon />}
                    subtitle="Total smart contract games entered"
                    onClick={() => setActiveTab("lotteries")}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <MetricCard
                    title="Purchased Gift Cards"
                    value={giftCardList.length}
                    icon={<GiftCardIcon />}
                    subtitle="Active and redeemed BetCards"
                    onClick={() => setActiveTab("giftcards")}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <MetricCard
                    title="Referral Network"
                    value={`${Number(referralBonusEarned).toFixed(2)} ${currency}`}
                    icon={<ReferralIcon />}
                    subtitle={copiedReferral ? "Link copied to clipboard!" : "5% lifetime player commission"}
                    onClick={() => handleCopy(referralUrl, "referral")}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {/* TAB 2: MY LOTTERIES */}
          {activeTab === "lotteries" && (
            <Box>
              {/* Toolbar */}
              <Box
                sx={{
                  p: 2.5,
                  mb: 3,
                  bgcolor: colors.background.paper,
                  borderRadius: borderRadiusPx.xl,
                  border: `1px solid ${colors.dashboard.cardBorder}`,
                  boxShadow: customShadows.cardSubtle,
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={6}>
                    <CommonInput
                      placeholder="Search lotteries by ID or game type..."
                      value={lotterySearch}
                      onChange={(e) => setLotterySearch(e.target.value)}
                      fullWidth
                      size="small"
                      startAdornment={<Search size={18} color={colors.slate[400]} />}
                    />
                  </Grid>

                  <Grid item xs={8} sm={4} md={4}>
                    <CommonSelect
                      value={lotteryTypeFilter}
                      onChange={(e) => setLotteryTypeFilter(e.target.value)}
                      options={[
                        { value: "all", label: "All Game Types" },
                        ...Object.entries(lotteryTypeMapper).map(([typeId, typeName]) => ({
                          value: String(typeId),
                          label: typeName,
                        })),
                      ]}
                      size="small"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={4} sm={2} md={2} sx={{ textAlign: "right" }}>
                    <CommonViewToggle
                      value={lotteryViewMode}
                      onChange={(m) => setLotteryViewMode(m)}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Table or Grid or Empty State */}
              {filteredLotteries.length === 0 ? (
                <Paper
                  elevation={0}
                  sx={{
                    p: 6,
                    textAlign: "center",
                    bgcolor: colors.background.paper,
                    borderRadius: borderRadiusPx.xl,
                    border: `1px dashed ${colors.dashboard.cardBorder}`,
                  }}
                >
                  <LotteryIcon sx={{ fontSize: 48, color: colors.slate[300], mb: 1.5 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: colors.slate[800], mb: 0.5 }}>
                    No Lotteries Found
                  </Typography>
                  <Typography sx={{ color: colors.slate[500], fontSize: "14px", mb: 2.5, maxWidth: "420px", mx: "auto" }}>
                    {lotterySearch || lotteryTypeFilter !== "all"
                      ? "No lotteries matched your active search query or game filter."
                      : "You have not entered or purchased any lottery tickets yet."}
                  </Typography>
                  <CommonButton
                    variant="primary"
                    startIcon={<LotteryIcon />}
                    onClick={() => history.push(Path.allLotteries)}
                  >
                    Browse All Lotteries
                  </CommonButton>
                </Paper>
              ) : lotteryViewMode === "grid" ? (
                <Grid container spacing={3}>
                  {filteredLotteries.map((item) => (
                    <Grid item xs={12} sm={6} lg={4} key={item.lotteryId}>
                      <AdminLotteryCard
                        lottery={item}
                        onViewDetails={(id) => history.push(Path.getLotteryDetails(id))}
                        userRole={activeDashboardRole === "admin" ? "admin" : "user"}
                      />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box
                  sx={{
                    bgcolor: colors.background.paper,
                    borderRadius: borderRadiusPx.xl,
                    border: `1px solid ${colors.dashboard.cardBorder}`,
                    boxShadow: customShadows.cardFlat,
                    overflow: "hidden",
                  }}
                >
                  <CommonTable
                    columns={lotteryColumns}
                    rows={filteredLotteries}
                    rowKey={(row, idx) => row.lotteryId || `lottery-${idx}`}
                    hover
                  />
                </Box>
              )}
            </Box>
          )}

          {/* TAB 3: MY GIFT CARDS */}
          {activeTab === "giftcards" && (
            <Box>
              {/* Toolbar */}
              <Box
                sx={{
                  p: 2.5,
                  mb: 3,
                  bgcolor: colors.background.paper,
                  borderRadius: borderRadiusPx.xl,
                  border: `1px solid ${colors.dashboard.cardBorder}`,
                  boxShadow: customShadows.cardSubtle,
                }}
              >
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={7} md={8}>
                    <CommonInput
                      placeholder="Search gift cards by ID or token name..."
                      value={giftCardSearch}
                      onChange={(e) => setGiftCardSearch(e.target.value)}
                      fullWidth
                      size="small"
                      startAdornment={<Search size={18} color={colors.slate[400]} />}
                    />
                  </Grid>

                  <Grid item xs={12} sm={5} md={4}>
                    <CommonSelect
                      value={giftCardTypeFilter}
                      onChange={(e) => setGiftCardTypeFilter(e.target.value)}
                      options={[
                        { value: "all", label: "All Card Types" },
                        { value: "sell", label: "Standard Cards" },
                        { value: "donation", label: "Donation Cards" },
                      ]}
                      size="small"
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Table or Empty State */}
              {filteredGiftCards.length === 0 ? (
                <Paper
                  elevation={0}
                  sx={{
                    p: 6,
                    textAlign: "center",
                    bgcolor: colors.background.paper,
                    borderRadius: borderRadiusPx.xl,
                    border: `1px dashed ${colors.dashboard.cardBorder}`,
                  }}
                >
                  <GiftCardIcon sx={{ fontSize: 48, color: colors.slate[300], mb: 1.5 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: colors.slate[800], mb: 0.5 }}>
                    No Gift Cards Found
                  </Typography>
                  <Typography sx={{ color: colors.slate[500], fontSize: "14px", mb: 2.5, maxWidth: "420px", mx: "auto" }}>
                    {giftCardSearch || giftCardTypeFilter !== "all"
                      ? "No gift cards matched your active filters or keyword search."
                      : "You have not purchased or received any AutoBET gift cards yet."}
                  </Typography>
                  <CommonButton
                    variant="primary"
                    startIcon={<GiftCardIcon />}
                    onClick={() => history.push(Path.allgiftcards)}
                  >
                    Explore Gift Cards
                  </CommonButton>
                </Paper>
              ) : (
                <Box
                  sx={{
                    bgcolor: colors.background.paper,
                    borderRadius: borderRadiusPx.xl,
                    border: `1px solid ${colors.dashboard.cardBorder}`,
                    boxShadow: customShadows.cardFlat,
                    overflow: "hidden",
                  }}
                >
                  <CommonTable
                    columns={giftCardColumns}
                    rows={filteredGiftCards}
                    rowKey={(row, idx) => row.giftCardId || `card-${idx}`}
                    hover
                  />
                </Box>
              )}
            </Box>
          )}

      {/* WITHDRAWAL CONFIRMATION DIALOG */}
      <CommonDialog
        open={withdrawModalOpen}
        onClose={() => !withdrawing && setWithdrawModalOpen(false)}
        title={
          withdrawType === "lottery"
            ? "Redeem Lottery BET Tokens"
            : withdrawType === "giftcard"
            ? "Withdraw Gift Card BET Tokens"
            : withdrawType === "referral"
            ? `Withdraw Referral ${currency} Commission`
            : withdrawType === "creator"
            ? "Withdraw Creator Ticket Commission"
            : "Withdraw Admin Platform Fees"
        }
        subtitle="Confirm blockchain transaction with your connected wallet"
        maxWidth="xs"
        actions={
          <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ width: "100%" }}>
            <CommonButton
              variant="cancel"
              onClick={() => setWithdrawModalOpen(false)}
              disabled={withdrawing}
            >
              Cancel
            </CommonButton>

            <CommonButton
              variant="primary"
              onClick={handleConfirmWithdraw}
              loading={withdrawing}
            >
              {withdrawing ? "Confirming..." : "Confirm Withdrawal"}
            </CommonButton>
          </Stack>
        }
      >
        <Box sx={{ py: 1.5 }}>
          <Typography variant="body2" sx={{ color: colors.slate[700], mb: 2 }}>
            You are about to transfer your accumulated rewards to your connected wallet:
          </Typography>

          <Box
            sx={{
              p: 2,
              borderRadius: borderRadiusPx.md,
              bgcolor: colors.slate[50],
              border: `1px solid ${colors.slate[200]}`,
              mb: 2,
            }}
          >
            <Typography variant="caption" sx={{ color: colors.slate[500], fontWeight: 600, display: "block" }}>
              TARGET WALLET ADDRESS
            </Typography>
            <Typography sx={{ fontFamily: "monospace", fontSize: "0.85rem", fontWeight: 700, color: colors.slate[900] }}>
              {account}
            </Typography>
          </Box>

          <Box
            sx={{
              p: 2,
              borderRadius: borderRadiusPx.md,
              bgcolor: colors.emerald[50],
              border: `1px solid ${colors.emerald[200]}`,
            }}
          >
            <Typography variant="caption" sx={{ color: colors.emerald[800], fontWeight: 600, display: "block" }}>
              TRANSFER AMOUNT
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, color: colors.emerald[700] }}>
              {withdrawType === "lottery"
                ? `${Number(lotteryTokensEarned).toFixed(4)} BET`
                : withdrawType === "giftcard"
                ? `${Number(giftCardTokensEarned).toFixed(4)} BET`
                : withdrawType === "referral"
                ? `${Number(referralBonusEarned).toFixed(4)} ${currency}`
                : withdrawType === "creator"
                ? `${Number(creatorAmountEarned).toFixed(4)} ${currency}`
                : `${Number(adminRegistrationFees).toFixed(4)} ${currency}`}
            </Typography>
          </Box>
        </Box>
      </CommonDialog>
    </DashboardLayout>
  );
};

export default Profile;
